import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Content calendar ("Redaktionsplan", page „Planung“): planned posts of an
 * organization. Nothing is posted to the networks; the plan organizes the work.
 */

export const PLAN_CHANNELS = [
  "instagram",
  "tiktok",
  "linkedin",
  "facebook",
  "youtube",
  "x",
  "other",
] as const;
export type PlanChannel = (typeof PLAN_CHANNELS)[number];

export const PLAN_CHANNEL_LABELS: Record<PlanChannel, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  facebook: "Facebook",
  youtube: "YouTube",
  x: "X",
  other: "Sonstiges",
};

/** Characters of a post text per channel (caption incl. hashtags); null = no limit. */
export const PLAN_CHANNEL_TEXT_LIMITS: Record<PlanChannel, number | null> = {
  instagram: 2200,
  tiktok: 2200,
  linkedin: 3000,
  facebook: 63206,
  youtube: 5000,
  x: 280,
  other: null,
};

/** Hashtags that a channel accepts at most (null = no fixed limit). */
export const PLAN_CHANNEL_HASHTAG_LIMITS: Partial<Record<PlanChannel, number>> = {
  instagram: 30,
};

export const PLAN_STATUSES = ["idea", "in_progress", "ready", "published"] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

export const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  idea: "Idee",
  in_progress: "In Arbeit",
  ready: "Bereit",
  published: "Veröffentlicht",
};

/** Characters as the networks count them (emoji and umlauts are one each). */
export function postTextLength(text: string): number {
  return [...text].length;
}

export function hashtagCount(text: string): number {
  return text.match(/(^|\s)#[\p{L}\p{N}_]+/gu)?.length ?? 0;
}

export const plannedPostSchema = z.object({
  id: z.string(),
  scheduledAt: z.date(),
  channel: z.enum(PLAN_CHANNELS),
  status: z.enum(PLAN_STATUSES),
  title: z.string(),
  caption: z.string(),
  notes: z.string(),
  document: z
    .object({
      id: z.string(),
      title: z.string(),
      kind: z.enum(["design", "video"]),
      projectId: z.string(),
      thumbnailUrl: z.string().nullable(),
    })
    .nullable(),
  assignee: z.object({ id: z.string(), name: z.string() }).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type PlannedPost = z.infer<typeof plannedPostSchema>;

const org = { orgId: z.string().min(1) };
const fields = {
  scheduledAt: z.coerce.date(),
  channel: z.enum(PLAN_CHANNELS),
  status: z.enum(PLAN_STATUSES),
  title: z.string().trim().min(1, "Bitte einen Titel angeben").max(200),
  caption: z.string().max(5000),
  notes: z.string().max(5000),
  documentId: z.uuid().nullable(),
  assigneeId: z.string().min(1).nullable(),
};

export const plannedPostInputSchema = z.object({
  ...fields,
  status: fields.status.default("idea"),
  caption: fields.caption.default(""),
  notes: fields.notes.default(""),
  documentId: fields.documentId.default(null),
  assigneeId: fields.assigneeId.default(null),
});
export type PlannedPostInput = z.infer<typeof plannedPostInputSchema>;
export const plannedPostPatchSchema = z.object(fields).partial();
export type PlannedPostPatch = z.infer<typeof plannedPostPatchSchema>;

/** Longest range one list request covers (a month view with its edge weeks fits). */
export const PLAN_MAX_RANGE_DAYS = 100;

export const contentPlanContract = {
  list: oc
    .input(
      z.object({
        ...org,
        from: z.coerce.date(),
        to: z.coerce.date(),
        channels: z.array(z.enum(PLAN_CHANNELS)).optional(),
        statuses: z.array(z.enum(PLAN_STATUSES)).optional(),
      }),
    )
    .output(z.array(plannedPostSchema)),
  create: oc.input(z.object({ ...org }).and(plannedPostInputSchema)).output(plannedPostSchema),
  update: oc
    .input(z.object({ ...org, id: z.uuid() }).and(plannedPostPatchSchema))
    .output(plannedPostSchema),
  remove: oc.input(z.object({ ...org, id: z.uuid() })).output(z.object({ ok: z.literal(true) })),
};
